/**
 * NEONELLE - SECTION 02: PRODUCT SHOWCASE JS
 * Interactive color switcher, multi-angle thumbnails, size selector, bundle tiers, accordions, size modal, mobile sticky buy bar & AJAX cart
 */

class NeonelleProductShowcase extends HTMLElement {
  constructor() {
    super();
    this.colorButtons = this.querySelectorAll('[data-showcase-color]');
    this.galleryLayers = this.querySelectorAll('.neonelle-gallery-layer');
    this.thumbButtons = this.querySelectorAll('.neonelle-thumb-btn');
    this.thumbHeroImg = this.querySelector('#NeonelleThumbHero');
    this.sizeButtons = this.querySelectorAll('[data-size-val]');
    this.colorNameOutput = this.querySelector('[data-color-output]');
    this.stockOutput = this.querySelector('[data-stock-count]');
    this.badgePill = this.querySelector('.neonelle-gallery-badge-pill');
    this.addBtn = this.querySelector('#NeonelleAddToBagBtn');
    this.expressBuyBtn = this.querySelector('#NeonelleExpressBuyBtn');
    this.accordionItems = this.querySelectorAll('.neonelle-accordion-item');
    this.bundleCards = this.querySelectorAll('.neonelle-bundle-card');
    this.activeColor = 'pink';
    this.activeSize = '7';
    this.selectedBundleQty = 1;
    this.selectedBundlePrice = '95.95';
    this.selectedBundleDiscount = 0;

    this.colorPalettes = {
      pink: {
        color: '#ff1475',
        glow: 'rgba(255, 20, 117, 0.6)',
        name: 'SIGNATURE PINK',
        stock: '9',
        heroImg: 'neonelle_hero_pink.jpg'
      },
      green: {
        color: '#22c55e',
        glow: 'rgba(34, 197, 94, 0.6)',
        name: 'CYBER GREEN',
        stock: '6',
        heroImg: 'neonelle_hero_green.jpg'
      },
      purple: {
        color: '#a855f7',
        glow: 'rgba(168, 85, 247, 0.6)',
        name: 'ULTRAVIOLET',
        stock: '4',
        heroImg: 'neonelle_hero_purple.jpg'
      },
      blue: {
        color: '#00d4ff',
        glow: 'rgba(0, 212, 255, 0.6)',
        name: 'ELECTRIC CYAN',
        stock: '7',
        heroImg: 'neonelle_hero_blue.jpg'
      }
    };
  }

  connectedCallback() {
    this.bindColors();
    this.bindThumbnails();
    this.bindSizes();
    this.bindBundleTiers();
    this.bindAccordions();
    this.bindSizeModal();
    this.bindCart();
    this.bindGlowToggle();
    this.bindSwipe();
    this.bindGalleryArrows();
    this.initControlsFade();

    window.neonelleExpressBuy = async () => {
      await this.addToCart(true);
    };

    window.neonelleShareWithBestie = async (e, pairCount = 2) => {
      e.preventDefault();
      const btn = e.currentTarget;
      const originalText = btn.innerHTML;
      const isTrio = pairCount === 3;
      const targetLabel = isTrio ? 'BESTIES' : 'BESTIE';
      const discountText = isTrio ? '25% OFF' : '15% OFF';
      const shareUrl = `${window.location.origin}${window.location.pathname}?bundle=${pairCount}`;
      const shareTitle = `Neonelle LED Boots — Pick Your Color & Size (${discountText})`;
      const shareText = isTrio 
        ? `✨ Hey besties! Pick your boot color & size for our Neonelle LED Cowboy Boots festival squad! We get 25% OFF together here: ${shareUrl}`
        : `✨ Hey bestie! Pick your boot color & size for our Neonelle LED Cowboy Boots duo! We get 15% OFF together here: ${shareUrl}`;

      // 1. Native Web Share API (WhatsApp, iMessage, Instagram, Messenger, etc.)
      if (navigator.share) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: shareUrl
          });
          btn.innerHTML = `<span>✓ SHARED TO ${targetLabel}!</span>`;
          btn.style.color = '#22C55E';
          btn.style.borderColor = '#22C55E';
          setTimeout(() => {
            btn.innerHTML = originalText;
            btn.style.color = '';
            btn.style.borderColor = '';
          }, 3000);
          return;
        } catch (err) {
          if (err.name === 'AbortError') return; // User dismissed share sheet
        }
      }

      // 2. Clipboard Fallback
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          btn.innerHTML = `<span>✓ LINK COPIED! SEND TO ${targetLabel}</span>`;
          btn.style.color = '#22C55E';
          btn.style.borderColor = '#22C55E';
          setTimeout(() => {
            btn.innerHTML = originalText;
            btn.style.color = '';
            btn.style.borderColor = '';
          }, 3000);
        });
      }
    };
  }

  initControlsFade() {
    const arrows = this.querySelectorAll('.neonelle-gallery-arrow');
    const pagination = this.querySelector('.neonelle-gallery-pagination');
    const stage = this.querySelector('.neonelle-gallery-stage');
    if (!stage) return;

    let fadeTimeout = null;

    const showControls = () => {
      arrows.forEach(a => a.classList.remove('is-faded-out'));
      if (pagination) pagination.classList.remove('is-faded-out');

      if (fadeTimeout) clearTimeout(fadeTimeout);
      fadeTimeout = setTimeout(() => {
        arrows.forEach(a => a.classList.add('is-faded-out'));
        if (pagination) pagination.classList.add('is-faded-out');
      }, 2000);
    };

    showControls();

    stage.addEventListener('touchstart', showControls, { passive: true });
    stage.addEventListener('touchmove', showControls, { passive: true });
    stage.addEventListener('mousemove', showControls, { passive: true });
    stage.addEventListener('click', showControls, { passive: true });
  }

  bindGalleryArrows() {
    const prevBtn = this.querySelector('[data-gallery-action="prev"]');
    const nextBtn = this.querySelector('[data-gallery-action="next"]');

    const getActiveThumbIndex = () => {
      const activeBtn = this.querySelector('.neonelle-thumb-btn.is-active');
      const allThumbs = Array.from(this.thumbButtons);
      const idx = allThumbs.indexOf(activeBtn);
      return idx >= 0 ? idx : 0;
    };

    const goToThumbIndex = (index) => {
      const allThumbs = Array.from(this.thumbButtons);
      if (allThumbs.length === 0) return;
      if (index < 0) index = allThumbs.length - 1;
      if (index >= allThumbs.length) index = 0;

      allThumbs[index].click();
      allThumbs[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToThumbIndex(getActiveThumbIndex() - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToThumbIndex(getActiveThumbIndex() + 1);
      });
    }
  }

  updatePagination(index) {
    const dots = this.querySelectorAll('.neonelle-pag-dot');
    dots.forEach((dot, idx) => {
      dot.classList.toggle('is-active', idx === index);
    });
  }

  bindSwipe() {
    const stage = this.querySelector('.neonelle-gallery-stage');
    if (!stage) return;

    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    let isSwiping = false;

    const getActiveThumbIndex = () => {
      const activeBtn = this.querySelector('.neonelle-thumb-btn.is-active');
      const allThumbs = Array.from(this.thumbButtons);
      const idx = allThumbs.indexOf(activeBtn);
      return idx >= 0 ? idx : 0;
    };

    const goToThumbIndex = (index) => {
      const allThumbs = Array.from(this.thumbButtons);
      if (allThumbs.length === 0) return;
      if (index < 0) index = allThumbs.length - 1;
      if (index >= allThumbs.length) index = 0;

      allThumbs[index].click();
      allThumbs[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    stage.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        endX = startX;
        endY = startY;
        isSwiping = true;
      }
    }, { passive: true });

    stage.addEventListener('touchmove', (e) => {
      if (!isSwiping || e.touches.length !== 1) return;
      endX = e.touches[0].clientX;
      endY = e.touches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchend', () => {
      if (!isSwiping) return;
      isSwiping = false;
      const diffX = startX - endX;
      const diffY = startY - endY;

      // Minimum swipe distance of 35px, horizontal dominant
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 35) {
        const currentIndex = getActiveThumbIndex();
        if (diffX > 0) {
          // Swiped Left -> Next image
          goToThumbIndex(currentIndex + 1);
        } else {
          // Swiped Right -> Previous image
          goToThumbIndex(currentIndex - 1);
        }
      }
    }, { passive: true });
  }

  bindGlowToggle() {
    const stage = this.querySelector('.neonelle-gallery-stage');
    const glowBtns = this.querySelectorAll('.glow-mode-btn');
    if (!stage || glowBtns.length === 0) return;

    glowBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.glowMode;
        glowBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        if (mode === 'daylight') {
          stage.classList.add('is-daylight-mode');
        } else {
          stage.classList.remove('is-daylight-mode');
        }
      });
    });
  }

  bindColors() {
    this.colorButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetColor = btn.dataset.showcaseColor;
        this.selectColor(targetColor);
      });
    });
  }

  selectColor(colorKey) {
    if (!this.colorPalettes[colorKey]) return;
    this.activeColor = colorKey;
    const config = this.colorPalettes[colorKey];

    this.style.setProperty('--active-product-color', config.color);
    this.style.setProperty('--active-product-glow', config.glow);

    this.colorButtons.forEach((b) => {
      b.classList.toggle('is-selected', b.dataset.showcaseColor === colorKey);
    });

    this.galleryLayers.forEach((layer) => {
      const isMatch = (layer.dataset.galleryColor === colorKey) || (layer.dataset.colorLayer === colorKey);
      layer.classList.toggle('is-active', isMatch);
    });

    if (this.colorNameOutput) this.colorNameOutput.textContent = config.name;
    if (this.stockOutput) this.stockOutput.textContent = config.stock;
    if (this.badgePill) this.badgePill.textContent = `${config.name} EDITION`;

    this.thumbButtons.forEach((t) => t.classList.remove('is-active'));
    const heroThumb = this.querySelector('[data-thumb-target="hero"]');
    if (heroThumb) {
      heroThumb.classList.add('is-active');
      const activeLayerImg = this.querySelector(`.neonelle-gallery-layer[data-gallery-color="${colorKey}"] img`);
      if (activeLayerImg && this.thumbHeroImg) {
        this.thumbHeroImg.src = activeLayerImg.src;
      }
    }
    this.updatePagination(0);

    // Sync sticky bar
    if (this.stickyThumbImg) {
      const activeLayerImg = this.querySelector(`.neonelle-gallery-layer[data-gallery-color="${colorKey}"] img`);
      if (activeLayerImg) this.stickyThumbImg.src = activeLayerImg.src;
    }
    this.stickyDots.forEach((dot) => {
      dot.classList.toggle('is-active', dot.dataset.stickyColor === colorKey);
    });
  }

  bindThumbnails() {
    this.thumbButtons.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.thumbTarget;
        this.thumbButtons.forEach((t) => t.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.updatePagination(idx);

        this.galleryLayers.forEach((layer) => {
          if (target === 'hero') {
            const isMatch = (layer.dataset.galleryColor === this.activeColor) || (layer.dataset.colorLayer === this.activeColor);
            layer.classList.toggle('is-active', isMatch);
          } else {
            const isMatch = (layer.dataset.galleryAngle === target) || (layer.dataset.thumbTarget === target);
            layer.classList.toggle('is-active', isMatch);
          }
        });
      });
    });
  }

  bindSizes() {
    this.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selectSize(btn.dataset.sizeVal);
      });
    });
  }

  selectSize(sizeVal) {
    this.activeSize = sizeVal;
    this.sizeButtons.forEach((b) => {
      b.classList.toggle('is-active', b.dataset.sizeVal === sizeVal);
    });
    if (this.stickySizeSelect) {
      this.stickySizeSelect.value = sizeVal;
    }
  }

  bindBundleTiers() {
    this.bundleCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') {
          return; // Let user change select value without disrupting selection
        }
        this.bundleCards.forEach((c) => c.classList.remove('is-selected'));
        card.classList.add('is-selected');

        this.selectedBundleQty = parseInt(card.dataset.bundleQty || '1', 10);
        this.selectedBundlePrice = card.dataset.bundlePrice || '95.95';
        this.selectedBundleDiscount = parseInt(card.dataset.bundleDiscount || '0', 10);

        this.updateButtonLabels();
      });

      // Stop propagation on select elements & sync preview thumb
      const selects = card.querySelectorAll('.neonelle-bundle-select');
      selects.forEach((select) => {
        select.addEventListener('click', (e) => e.stopPropagation());
      });

      const colorSelects = card.querySelectorAll('.neonelle-bundle-select[data-select-type="color"]');
      colorSelects.forEach((colorSelect) => {
        colorSelect.addEventListener('change', (e) => {
          const pairRow = colorSelect.closest('.neonelle-bundle-pair-row');
          const thumbImg = pairRow ? pairRow.querySelector('.pair-preview-thumb') : null;
          if (thumbImg) {
            const val = colorSelect.value.toLowerCase();
            let key = 'pink';
            if (val.includes('green')) key = 'green';
            else if (val.includes('purple') || val.includes('ultra')) key = 'purple';
            else if (val.includes('cyan') || val.includes('blue')) key = 'blue';

            const activeHero = this.querySelector(`.neonelle-gallery-layer[data-gallery-color="${key}"] img`);
            if (activeHero) {
              thumbImg.src = activeHero.src;
            }
            const config = this.colorPalettes[key];
            if (config) {
              thumbImg.style.borderColor = config.color;
              thumbImg.style.boxShadow = `0 0 8px ${config.glow}`;
            }
          }
        });
      });
    });
  }

  updateButtonLabels() {
    if (!this.addBtn) return;
    if (this.selectedBundleQty === 1) {
      this.addBtn.innerHTML = `<span>ADD TO BAG — $95.95</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`;
    } else {
      this.addBtn.innerHTML = `<span>ADD ${this.selectedBundleQty} PAIRS TO BAG — $${this.selectedBundlePrice} (SAVE ${this.selectedBundleDiscount}%)</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`;
    }
  }

  bindAccordions() {
    this.accordionItems.forEach((item) => {
      const trigger = item.querySelector('.neonelle-accordion-btn');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const wasOpen = item.classList.contains('is-open');
        this.accordionItems.forEach((i) => i.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });
  }

  bindSizeModal() {
    const trigger = this.querySelector('#NeonelleOpenSizeModalBtn');
    const backdrop = this.querySelector('#NeonelleSizeModalBackdrop');
    const closeBtn = this.querySelector('#NeonelleCloseSizeModalBtn');

    if (!trigger || !backdrop) return;

    const openModal = () => {
      backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
      backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    trigger.addEventListener('click', openModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && backdrop.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  async addToCart(isExpress = false) {
    const form = document.getElementById('NeonelleNativeProductForm');
    let properties = {};

    let noteSummary = '';
    if (this.selectedBundleQty === 1) {
      const colorName = this.colorPalettes[this.activeColor] ? this.colorPalettes[this.activeColor].name : 'SIGNATURE PINK';
      properties = {
        'Colorway': colorName,
        'Size': `US ${this.activeSize}`,
        'Selected Item': `${colorName} / US ${this.activeSize}`
      };
      noteSummary = `1 Pair: ${colorName} (US ${this.activeSize})`;
    } else {
      const activeCard = this.querySelector(`.neonelle-bundle-card[data-bundle-qty="${this.selectedBundleQty}"]`);
      let pairsList = [];
      if (activeCard) {
        for (let i = 1; i <= this.selectedBundleQty; i++) {
          const colorSel = activeCard.querySelector(`[data-pair-idx="${i}"][data-select-type="color"]`);
          const sizeSel = activeCard.querySelector(`[data-pair-idx="${i}"][data-select-type="size"]`);
          const colorVal = colorSel ? colorSel.value : (i === 1 ? 'Signature Pink' : (i === 2 ? 'Cyber Green' : 'Ultraviolet'));
          const sizeVal = sizeSel ? sizeSel.value : 'US 7';
          properties[`Pair ${i}`] = `${colorVal} (${sizeVal})`;
          pairsList.push(`Pair ${i}: ${colorVal} (${sizeVal})`);
        }
      }
      properties['Bundle Offer'] = `${this.selectedBundleQty} Pairs (${this.selectedBundleDiscount}% OFF)`;
      noteSummary = pairsList.join(' | ');
    }

    let variantId = null;
    try {
      const prodJsonEl = document.getElementById('NeonelleProductJson');
      if (prodJsonEl && prodJsonEl.textContent) {
        const prodData = JSON.parse(prodJsonEl.textContent);
        if (prodData && prodData.variants && prodData.variants.length > 0) {
          const availVariant = prodData.variants.find((v) => v.available) || prodData.variants[0];
          if (availVariant) {
            variantId = availVariant.id;
          }
        }
      }
    } catch(e) {}

    if (!variantId) {
      const nativeInp = document.getElementById('NeonelleNativeVariantId');
      if (nativeInp && nativeInp.value) variantId = parseInt(nativeInp.value, 10);
    }

    if (!variantId) variantId = 47087711977644;

    // Populate native inputs
    const qtyInp = document.getElementById('NeonelleNativeQuantity');
    if (qtyInp) qtyInp.value = this.selectedBundleQty;

    const propsWrap = document.getElementById('NeonelleDynamicProperties');
    if (propsWrap) {
      propsWrap.innerHTML = '';
      for (const [k, v] of Object.entries(properties)) {
        const h = document.createElement('input');
        h.type = 'hidden';
        h.name = `properties[${k}]`;
        h.value = v;
        propsWrap.appendChild(h);
      }
    }

    const payload = {
      items: [{
        id: Number(variantId),
        quantity: Number(this.selectedBundleQty),
        properties: properties
      }]
    };

    if (isExpress) {
      // 1. Add to cart via API with full line item properties
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch(e) {}

      // 2. Direct checkout URL with discounts and note params
      let checkoutParams = [];
      if (this.selectedBundleQty === 2) checkoutParams.push('discount=BUNDLE15');
      else if (this.selectedBundleQty === 3) checkoutParams.push('discount=BUNDLE25');
      
      for (const [k, v] of Object.entries(properties)) {
        checkoutParams.push(`attributes[${encodeURIComponent(k)}]=${encodeURIComponent(v)}`);
      }
      if (noteSummary) checkoutParams.push(`note=${encodeURIComponent(noteSummary)}`);

      let finalCheckoutUrl = '/checkout';
      if (checkoutParams.length > 0) finalCheckoutUrl += '?' + checkoutParams.join('&');
      window.location.href = finalCheckoutUrl;
      return;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        this.addBtn.innerHTML = '<span>ADDED TO BAG</span>';
        if (this.stickyAddBtn) {
          this.stickyAddBtn.querySelector('.sticky-btn-title').textContent = 'ADDED';
        }

        // Open Right-Side Cart Drawer
        if (window.NeonelleCart && typeof window.NeonelleCart.open === 'function') {
          window.NeonelleCart.open();
        } else {
          window.dispatchEvent(new CustomEvent('neonelle:cart:open'));
        }

        setTimeout(() => {
          this.updateButtonLabels();
          this.addBtn.disabled = false;
          if (this.stickyAddBtn) {
            this.stickyAddBtn.querySelector('.sticky-btn-title').textContent = 'ADD TO BAG';
          }
        }, 1200);
      } else {
        const errorData = await response.json();
        const msg = errorData.description || errorData.message || 'Error adding product to cart';
        alert(`Shopify Notice: ${msg}\n\nPlease check Settings > Markets / Shipping in Shopify Admin to ensure the United States zone is enabled.`);
        this.updateButtonLabels();
        this.addBtn.disabled = false;
      }
    } catch (err) {
      this.updateButtonLabels();
      this.addBtn.disabled = false;
    }
  }

  bindCart() {
    if (this.addBtn) {
      this.addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        this.addBtn.classList.add('is-loading');
        this.addBtn.disabled = true;
        await this.addToCart(false);
        this.addBtn.classList.remove('is-loading');
      });
    }

    if (this.expressBuyBtn) {
      this.expressBuyBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        this.expressBuyBtn.classList.add('is-loading');
        this.expressBuyBtn.disabled = true;
        await this.addToCart(true);
      });
    }
  }
}

if (!customElements.get('neonelle-product-showcase')) {
  customElements.define('neonelle-product-showcase', NeonelleProductShowcase);
}
